<script>
    import { tick } from 'svelte';
    import { mapMultiSelectMode, mapPopupOpen, mapPlacingPlayground, mapInUse } from './stores/stores';
    import { keyScope, releaseModalScopes } from './scripts/keyboard';

    import L from 'leaflet';
    import MarkerPopup from './popups/MarkerPopup.svelte';
    import ApproveEditPopup from './popups/ApproveEditPopup.svelte';
    import EditPopup from './popups/EditPopup.svelte';
    import PhotoPopup from './popups/PhotoPopup.svelte';
    import PhotoGroupPopup from './popups/PhotoGroupPopup.svelte';
    import ApprovePhotoEditPopup from './popups/ApprovePhotoEditPopup.svelte';
    import ApproveRemovalPopup from './popups/ApproveRemovalPopup.svelte';
    import 'leaflet/dist/leaflet.css';

    import Regions from './scripts/regions';
    import PlaygroundDataProvider from './scripts/playgrounddataprovider';
    import PlaygroundHelper from './scripts/playgroundhelper.js';
    import GeoUtils from './scripts/geoutils';
    import ApiClient, { SessionExpiredError } from './scripts/apiclient';
    import { createActionRunner } from './scripts/ActionRunner';
    import MapSelectionDialog from './MapSelectionDialog.svelte';
    import SearchBox from './SearchBox.svelte';

    const { busy: selectionBusy, run: runSelectionAction } = createActionRunner();

    /**
     * How far in to go when the map is taken to a single playground. Close enough to tell
     * its marker from the ones around it and to see where it sits.
     */
    const playgroundZoom = 16;

    /**
     * How far in to go when the map is taken to a place. A town is the whole of itself
     * rather than a point in it, so this stops far enough out to show it.
     */
    const placeZoom = 13;

    /**
     * The strip along the top of the map to keep a dialog out of, as the space the search
     * field needs. The field is 44px tall at 10px down, and the rest is daylight below it.
     * Written against the layout in SearchBox.svelte.
     */
    const searchFieldStrip = 64;

    /**
     * Whether the search field stays up while a dialog is open, which is wherever there is
     * room for both of them. Below the breakpoint of the shell a dialog is nearly as wide
     * as the screen and the field steps aside for it, so there is nothing to keep clear of
     * and the room is better spent on the dialog. The field decides the same thing for
     * itself, in a media query written against this breakpoint.
     */
    function searchFieldSharesTheMap() {
        return window.matchMedia('(min-width: 992px)').matches;
    }

    // The Leaflet map
    let map;

    /**
     * The current map markers
     */
    let markers = [];

    let selected = [];

    // Clear selection when multi select mode ends
    $: if ($mapMultiSelectMode == false && selected.length > 0) {
        clearSelected();
    }

    // Close open popups when multi select mode is engaged
    $: if ($mapMultiSelectMode == true) {
        map.closePopup();
    }

    /**
     * A callback that can be set when the map is scrolled, and called once
     * playgrounds have been loaded for the new location. The callback is
     * called once and then removed.
     *
     * @type {Function}
     */
    var scrollLoadCallback = null;

    /**
     * The initial position of the map.
     */
    export let startingPosition;

    /**
     * Temporary marker used e.g. when adding new playgrounds.
     */
    let temporaryMarker = null;

    let autoReloadEnabled = true;

    /**
     * The dialog open on the map, if there is one. Kept so that it can be measured and
     * placed again once the map changes size under it.
     */
    let currentPopup = null;

    // The shortcuts of the map itself, available whenever no dialog is open
    keyScope({
        Enter: pressEnter,
        Escape: pressEscape,
        m: () => {
            if (!$mapMultiSelectMode) {
                $mapMultiSelectMode = true;
            }
        },
    });

    /**
     * Set up the map
     * @param container
     */
    function createMap(container) {
        let map = L.map(container, {
            preferCanvas: true,
            attributionControl: false,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            subdomains: 'abc',
        }).addTo(map);

        positionMap(map);

        // Events
        map.on('zoomend', () => {
            mapUpdated(map);
        });

        map.on('moveend', () => {
            mapUpdated(map);
        });

        let urlParams = new URLSearchParams(window.location.search);
        let showId = urlParams.get('show');

        if (showId) {
            ApiClient.loadPlayground(showId).then((playground) => {
                scrollToPlaygroundPopup(playground);
            });
        } else {
            loadPlaygrounds(map);
        }

        return map;
    }

    function addToSelected(playground) {
        if (selected.some((p) => p._id == playground._id)) {
            return false;
        }

        selected.push(playground);
        selected = selected;

        return true;
    }

    function clearSelected() {
        selected.forEach((p) => {
            let marker = getMarkerForPlayground(p);
            reloadMarker(marker);
        });

        selected = [];
    }

    function positionMap(map) {
        if (startingPosition) {
            return scrollMapToStartingPosition(startingPosition, map);
        }

        focusOnSweden(map);
    }

    export function getPosition() {
        let center = map.getCenter();

        return {
            position: {
                lat: center.lat,
                lon: center.lng,
            },
            zoom: map.getZoom(),
        };
    }

    export function scrollMapToPlayground(playground, callback) {
        let location = GeoUtils.getPlaygroundLocation(playground);

        if (!location) {
            return;
        }

        scrollMap(location, callback);
    }

    /**
     * Scrolls the map to a location.
     *
     * @param {Object} location Where to go, with lat and lon.
     * @param {Function} callback Called once playgrounds have been loaded for the new
     *                            location, if anything needs to wait for them.
     * @param {Number} zoom How far in to go. Close enough to work on a single playground
     *                      unless told otherwise.
     */
    export function scrollMap(location, callback, zoom = playgroundZoom) {
        if (!location) {
            return;
        }

        autoReloadEnabled = true;

        scrollLoadCallback = callback;
        map.setView(location, zoom);
    }

    export function scrollMapToStartingPosition(startingPosition, aMap) {
        autoReloadEnabled = true;
        let theMap = aMap || map;
        let pos = startingPosition.position;
        theMap.setView(new L.LatLng(pos.lat, pos.lon), startingPosition.zoom);
    }

    export function scrollToPlaygroundPopup(playground) {
        if (!playground) {
            return;
        }

        scrollMapToPlayground(playground, () => {
            let marker = getMarkerForPlayground(playground);
            if (!marker) {
                return;
            }

            openPlaygroundPopup(marker);
        });
    }

    /**
     * Where the search looks around: whatever part of the world is on show. Results are
     * sorted by their distance from there, so a name that exists in several places finds
     * the one being worked on rather than the largest one.
     *
     * @returns {Object} The centre of the map, with lat and lon, or null before there is a
     *                   map to ask.
     */
    function searchCenter() {
        if (!map) {
            return null;
        }

        let center = map.getCenter();

        return { lat: center.lat, lon: center.lng };
    }

    /**
     * Takes the map to a place picked in the search.
     *
     * @param {Object} location The location of the place, with lat and lon.
     */
    function goToPlace(location) {
        scrollMap(location, null, placeZoom);
    }

    /**
     * Takes the map to a playground picked in the search and opens its dialog, the same way
     * a playground linked to with ?show= is opened. A search result carries little more
     * than a name and a location, so the playground itself is loaded first.
     *
     * @param {String} id The id of the playground.
     */
    async function goToSearchedPlayground(id) {
        let playground;

        try {
            playground = await ApiClient.loadPlayground(id);
        } catch (err) {
            console.log('Could not load the playground that was searched for', err);

            // An expired session shows the login form, and asking the user to try again
            // here would only be misleading.
            if (!(err instanceof SessionExpiredError)) {
                alert('Lekplatsen kunde inte hämtas. Försök igen.');
            }

            return;
        }

        scrollToPlaygroundPopup(playground);
    }

    export function openPlaygroundPopup(marker) {
        let playground = marker && marker.props ? marker.props.playground : null;

        if (!playground) {
            return;
        }

        let popupOptions = popupWidth(700, 400);

        // Listen for changes in number of photos and reload marker if gone from having
        // to not having photos or the other way around
        let photosBefore = playground.numberOfPhotos;
        let popupClosed = () => {
            marker.off('popupclose', popupClosed);

            if (photosBefore != playground.numberOfPhotos) {
                reloadMarker(marker);
            }
        };
        marker.on('popupclose', popupClosed);

        let makePopup = (container) => {
            return new MarkerPopup({
                target: container,
                props: {
                    playground: playground,
                    edit: () => {
                        openEditPopup(marker, playground);
                    },
                    move: () => {
                        marker.closePopup();
                        makeMarkerMovable(marker, playground);
                    },
                    close: () => {
                        marker.closePopup();

                        if (playground.state != 'published') {
                            removeMarker(marker);
                        }
                    },
                },
            });
        };

        bindPopup(marker, makePopup, popupOptions);
        marker.openPopup();
    }

    export function openEditPopup(marker, playground) {
        let popupOptions = popupWidth(700, 400);

        let editFinished = () => {
            marker.closePopup();

            if (playground.state != 'published') {
                removeMarker(marker);
            }
        };

        let makePopup = (container) => {
            return new EditPopup({
                target: container,
                props: {
                    playground: playground,
                    closePopup: editFinished,
                },
            });
        };

        bindPopup(marker, makePopup, popupOptions);

        marker.openPopup();
    }

    export function openApprovePopup(playground, onCloseCallback) {
        removeTemporaryMarker();

        // Nothing appears until the map has scrolled and no dialog until the marker is
        // tapped, so the sidebar is sent away here rather than further in. From the tap on
        // the item this is all about where the playground sits, and the map is what has to
        // be seen. The location is checked first: without one there is nothing to scroll to
        // and the sidebar would be left hidden with nothing to show for it.
        if (!GeoUtils.getPlaygroundLocation(playground)) {
            return;
        }

        $mapPlacingPlayground = true;

        scrollMapToPlayground(playground, () => {
            let marker = handleTemporaryMarker(playground);

            marker.on('popupclose', () => {
                // If the edit was approved, add a permanent marker
                if (playground.state == 'published') {
                    addMarker(playground);
                }

                if (onCloseCallback) {
                    onCloseCallback();
                }

                setTemporaryMarker(null);
            });
        });
    }

    export function openApprovePhotoPopup(photo, onCloseCallback) {
        let playground = photo.playground;

        scrollMapToPlayground(playground, () => {
            let marker = getMarkerForPlayground(playground);
            if (!marker) {
                console.log(`No marker for ${playground}`);
                return;
            }

            let closePopup = () => {
                marker.closePopup();

                if (onCloseCallback) {
                    onCloseCallback();
                }
            };

            let popupOptions = popupWidth(700, 400);

            let makePopup = (container) => {
                return new PhotoPopup({
                    target: container,
                    props: {
                        photo: photo,
                        closePopup: closePopup,
                    },
                });
            };

            bindPopup(marker, makePopup, popupOptions);

            marker.openPopup();
        });
    }

    export function openApprovePhotoGroupPopup(group, onCloseCallback, onSplitCallback) {
        let playground = group.playground;

        scrollMapToPlayground(playground, () => {
            let marker = getMarkerForPlayground(playground);
            if (!marker) {
                console.log(`No marker for ${playground}`);
                return;
            }

            let closePopup = () => {
                marker.closePopup();

                if (onCloseCallback) {
                    onCloseCallback();
                }
            };

            // Splitting the group up doesn't handle any photos, so the close callback is
            // skipped here. The sidebar replaces the group with the separate photos instead.
            let splitGroup = () => {
                marker.closePopup();

                if (onSplitCallback) {
                    onSplitCallback();
                }
            };

            let popupOptions = popupWidth(700, 400);

            let makePopup = (container) => {
                return new PhotoGroupPopup({
                    target: container,
                    props: {
                        group: group,
                        closePopup: closePopup,
                        splitGroup: splitGroup,
                    },
                });
            };

            bindPopup(marker, makePopup, popupOptions);

            marker.openPopup();
        });
    }

    export function openApprovePhotoEditPopup(photoEdit, onCloseCallback) {
        let playground = photoEdit.playground;

        scrollMapToPlayground(playground, () => {
            let marker = getMarkerForPlayground(playground);
            if (!marker) {
                console.log(`No marker for ${playground}`);
                return;
            }

            let closePopup = () => {
                marker.closePopup();

                if (onCloseCallback) {
                    onCloseCallback();
                }
            };

            let popupOptions = popupWidth(700, 400);

            let makePopup = (container) => {
                return new ApprovePhotoEditPopup({
                    target: container,
                    props: {
                        photoEdit: photoEdit,
                        closePopup: closePopup,
                    },
                });
            };

            bindPopup(marker, makePopup, popupOptions);

            marker.openPopup();
        });
    }

    export function openApproveEditPopup(edit, onCloseCallback) {
        let playground = edit.playground;
        scrollMapToPlayground(playground, () => {
            let marker = getMarkerForPlayground(playground);
            if (!marker) {
                console.log(`No marker for ${playground}`);
                return;
            }

            let popupOptions = popupWidth(500, 500);

            let closePopup = async () => {
                marker.closePopup();
                reloadMarker(marker);
                if (onCloseCallback) {
                    onCloseCallback();
                }
            };

            let makePopup = (container) => {
                return new ApproveEditPopup({
                    target: container,
                    props: {
                        edit: edit,
                        closePopup: closePopup,
                    },
                });
            };

            bindPopup(marker, makePopup, popupOptions);

            marker.openPopup();
        });
    }

    export function openApproveRemovalPopup(edit, onCloseCallback) {
        let playground = edit.playground;
        scrollMapToPlayground(playground, () => {
            let marker = getMarkerForPlayground(playground);
            if (!marker) {
                console.log(`No marker for ${playground}`);
                return;
            }

            let popupOptions = popupWidth(500, 500);

            let closePopup = async () => {
                marker.closePopup();
                reloadMarker(marker);
                if (onCloseCallback) {
                    onCloseCallback();
                }
            };

            let makePopup = (container) => {
                return new ApproveRemovalPopup({
                    target: container,
                    props: {
                        edit: edit,
                        closePopup: closePopup,
                    },
                });
            };

            bindPopup(marker, makePopup, popupOptions);

            marker.openPopup();
        });
    }

    function makeNewPlayground() {
        let center = map.getCenter();

        let playground = {
            location: {
                type: 'Point',
                coordinates: [center.lng, center.lat],
            },

            state: 'published',
        };

        return playground;
    }

    /**
     * Adds a new playground to the map.
     */
    export function addPlayground() {
        removeTemporaryMarker();

        let playground = makeNewPlayground();
        let marker = handleTemporaryMarker(playground);

        // If the playground was saved, add a permanent marker
        marker.on('popupclose', () => {
            if (playground._id) {
                addMarker(playground);
            }

            setTemporaryMarker(null);
        });
    }

    /**
     * Notes the marker a playground is being positioned with, or that there is no longer one,
     * and lets the shell lay itself out for it.
     *
     * @param {Object} marker The marker being positioned, or null once it is done with.
     */
    function setTemporaryMarker(marker) {
        temporaryMarker = marker;
        $mapPlacingPlayground = !!marker;
    }

    function handleTemporaryMarker(playground) {
        let marker = createTemporaryMarker(playground);
        marker.dragging.enable();
        setTemporaryMarker(marker);

        // Update the playground when the marker is moved
        marker.on('dragend', () => {
            let pos = marker.getLatLng();
            playground.location.coordinates = [pos.lng, pos.lat];
        });

        marker.on('click', (e) => {
            // Workaround for Leaflet Safari bug
            if (!e || !e.originalEvent || !e.originalEvent.isTrusted) {
                return;
            }

            // Once clicked, lock the marker
            marker.dragging.disable();

            openEditPopup(marker, playground);
        });

        return marker;
    }

    function makeMarkerMovable(marker) {
        let playground = marker.props.playground;
        if (!playground) {
            console.log(`No playground for ${marker}`);
            return;
        }

        let oldIcon = marker.getIcon();
        marker.setIcon(markerIcon('yellow'));
        marker.dragging.enable();

        let dragend = () => {
            let pos = marker.getLatLng();
            playground.location.coordinates = [pos.lng, pos.lat];

            ApiClient.updatePlayground(playground);

            marker.dragging.disable();
            marker.setIcon(oldIcon);

            marker.off('dragend', dragend);
        };

        marker.on('dragend', dragend);
    }

    function focusOnSweden(map) {
        var region = L.geoJSON(JSON.parse(Regions.sweden));
        map.fitBounds(region.getBounds());
    }

    function mapUpdated(map) {
        if (!autoReloadEnabled) {
            return;
        }

        if (map.getZoom() < 11) {
            return;
        }

        if (map.getZoom() < 10) {
            return;
        }

        loadPlaygrounds(map);
    }

    /**
     * Whether playgrounds are being loaded, and whether another load was asked for while
     * that was going on.
     *
     * A single setView fires both zoomend and moveend, and each of them asks for a load.
     * Run side by side they both find that data is needed, since neither has stored what it
     * loaded yet, and they both clear the map. The second one then does it after the first
     * has opened the popup that was waited for, taking away the marker holding it and
     * closing it right after it appeared. Run one after the other, the second finds the data
     * already loaded and leaves the map alone.
     */
    let loadingPlaygrounds = false;
    let loadPlaygroundsAgain = false;

    async function loadPlaygrounds(map) {
        if (loadingPlaygrounds) {
            loadPlaygroundsAgain = true;
            return;
        }

        loadingPlaygrounds = true;

        try {
            // Once a popup is open the map is left alone, the same way mapUpdated leaves it
            // alone, so that opening one can't be followed by a load that takes its marker
            // away. Leaflet pans the map to fit a popup when it opens, and that pan is
            // enough on its own to ask for another load.
            do {
                loadPlaygroundsAgain = false;
                await loadPlaygroundsOnce(map);
            } while (loadPlaygroundsAgain && autoReloadEnabled);
        } finally {
            loadingPlaygrounds = false;
        }
    }

    async function loadPlaygroundsOnce(map) {
        let pos = makeLocation(map.getCenter());
        const playgrounds = await PlaygroundDataProvider.loadPlaygrounds(pos);

        // No data needed
        if (playgrounds !== false) {
            clearMap();

            markers.push(...playgrounds.map((playground) => createMarker(playground)));
        }

        if (scrollLoadCallback) {
            scrollLoadCallback();
            scrollLoadCallback = null;
        }
    }

    function makeLocation(mapLocation) {
        return {
            lat: mapLocation.lat,
            lon: mapLocation.lng,
        };
    }

    function getMapLocation(playground) {
        return {
            lat: playground.location.coordinates[1],
            lng: playground.location.coordinates[0],
        };
    }

    function clearMap() {
        markers.forEach((marker) => map.removeLayer(marker));
        markers = [];
    }

    /**
     * The width to bind a dialog with. Leaflet holds a popup to at least minWidth and at
     * most maxWidth, and neither of them knows how much screen there is: a floor of 400px
     * puts a dialog out through the side of a 375px phone, and once it is out there is
     * nothing to bring it back.
     *
     * Measured against the window rather than the map. On a narrow screen the map is still
     * put away behind the inbox at the moment a dialog is bound and would measure nothing,
     * and by the time it is up it has the whole width anyway. What is taken off covers the
     * frame Leaflet draws around the content and leaves a little daylight at each side.
     *
     * @param {Number} max The width the dialog should not grow past.
     * @param {Number} min The width it would like to have at least, where there is room.
     * @returns {Object} Popup options to bind with.
     */
    function popupWidth(max, min) {
        let room = Math.max(document.documentElement.clientWidth - 60, 200);
        let options = { maxWidth: Math.min(max, room) };

        // Keep a dialog clear of the strip the search field sits in. Leaflet pans a dialog
        // into view as it opens, and without this it would come to rest under the field,
        // which draws above the map and would cover its header. Reserving the strip across
        // the whole width rather than the field's own corner is all the top padding can
        // say, and it keeps a dialog off the zoom buttons opposite into the bargain.
        if (searchFieldSharesTheMap()) {
            options.autoPanPaddingTopLeft = [10, searchFieldStrip];
        }

        if (min) {
            options.minWidth = Math.min(min, room);
        }

        return options;
    }

    // Create a popup with a Svelte component inside it and handle removal when the popup is torn down.
    // `makePopupComponent` will be called whenever the popup is being created, and should create and return the component.
    function bindPopup(marker, makePopupComponent, options) {
        marker.bindPopup(() => {
            let container = L.DomUtil.create('div');
            marker.props.popupComponent = makePopupComponent(container);
            return container;
        }, options);
    }

    function markerIcon(className) {
        let classNames = 'playground-marker';
        if (className) {
            classNames += ' ' + className;
        }

        return L.divIcon({
            className: 'map-marker',
            shadowSize: [0, 0],
            popupAnchor: [3, 0],
            html: `<div class="${classNames}"></div>`,
        });
    }

    function createBaseMarker(playground, icon) {
        let location = getMapLocation(playground);
        let options = { icon: icon, title: PlaygroundHelper.presentPlayground(playground) };
        let marker = L.marker(location, options).addTo(map);

        marker.props = {
            playground: playground,
        };

        marker.on('popupopen', (event) => {
            autoReloadEnabled = false;
            $mapPopupOpen = true;
            currentPopup = event.popup;
            event.popup.props = { popupComponent: marker.props.popupComponent };
        });

        marker.on('popupclose', (event) => {
            autoReloadEnabled = true;

            // Opening a dialog from another one closes the first and opens the second in one
            // go, so this is followed by an open before anything is drawn, and the sidebar
            // doesn't flash back in between the two.
            $mapPopupOpen = false;
            currentPopup = null;

            // The shortcuts of a dialog end with the dialog, not with its component, which
            // is kept around until the fade-out is over
            releaseModalScopes();

            let popup = event.popup;
            if (!popup.props || !popup.props.popupComponent) {
                return;
            }

            let old = popup.props.popupComponent;
            popup.props.popupComponent = null;
            // Wait to destroy until after the fadeout completes.
            setTimeout(() => {
                old.$destroy();

                // Since we're opening different popups all the time, binding just one doesn't make sense
                // TODO: This is a quick fix, can we open popups without binding at all?
                //
                // Check for another popup here rather than when the popup was closed: a new popup can
                // be bound to the marker while we wait, and unbinding it would leave it stuck open.
                let hasOpenedAnotherPopup = popup !== marker.getPopup();
                if (!hasOpenedAnotherPopup) {
                    marker.unbindPopup();
                }

                // Let others know the popup was removed
                marker.fire('popupremoved');
            }, 500);
        });

        return marker;
    }

    function createMarker(playground) {
        let complete = playground && (playground.description || (playground.features && playground.features.length > 0) || playground.numberOfPhotos > 0);
        let icon = markerIcon(complete ? 'green' : false);
        let marker = createBaseMarker(playground, icon);

        marker.on('click', (e) => {
            // Workaround for Leaflet Safari bug
            if (!e || !e.originalEvent || !e.originalEvent.isTrusted) {
                return;
            }

            // Multi select mode
            if ($mapMultiSelectMode) {
                if (addToSelected(playground)) {
                    marker.setIcon(markerIcon('yellow'));
                }
                return;
            }

            openPlaygroundPopup(marker);
        });

        return marker;
    }

    function createTemporaryMarker(playground) {
        let icon = markerIcon('yellow');
        let marker = createBaseMarker(playground, icon);

        // Remove the marker once the popup is dismissed
        marker.on('popupclose', () => {
            // Wait a little, otherwise removing the marker will trigger another popupclose
            setTimeout(() => {
                removeMarker(marker);
            }, 10);
        });

        return marker;
    }

    /**
     * Creates a new marker and adds it to the map as well as the markers array.
     */
    async function addMarker(playground) {
        let newMarker = createMarker(playground);
        markers.push(newMarker);
    }

    /**
     * Removes a marker from the map as well as from the markers array.
     * @param marker
     */
    function removeMarker(marker) {
        map.removeLayer(marker);
        let index = markers.indexOf(marker);
        if (index > -1) {
            markers.splice(index, 1);
        }
    }

    function removeTemporaryMarker() {
        if (!temporaryMarker) {
            return;
        }

        removeMarker(temporaryMarker);
        setTemporaryMarker(null);
    }

    /**
     * Replaces the specified marker with a new one. The new marker will use the specified playground
     * if there is one, otherwise it reloads the playground from the backend.
     *
     * @param {Object} marker The marker to reload/replace
     */
    async function reloadMarker(marker) {
        removeMarker(marker);

        let id = marker.props.playground._id;
        let playground = await ApiClient.loadPlayground(id);
        if (!playground || playground.state != 'published') {
            return;
        }

        addMarker(playground);
    }

    /**
     * Finds the marker for the specified playground.
     *
     * @param playground
     * @returns {Object} The matching marker, if one was found.
     */
    function getMarkerForPlayground(playground) {
        if (!playground || !playground._id) {
            return null;
        }

        return markers.find((m) => {
            if (!m.props || !m.props.playground || !m.props.playground._id) {
                return false;
            }

            return m.props.playground._id == playground._id;
        });
    }

    function mapAction(container) {
        map = createMap(container);

        return {
            destroy: () => {
                map.remove();
                map = null;
            },
        };
    }

    function resizeMap() {
        if (!map) {
            return;
        }

        map.invalidateSize();
    }

    /**
     * The last value acted on, so that the layout is only reported when it has really
     * changed rather than on every pass through the statement below.
     */
    let mapWasInUse = false;

    /**
     * Watches whether the map is on show. Anything can start that, a tap in the inbox, the
     * merge button in the toolbar, the keyboard, so this listens for the answer rather than
     * every one of them having to say so.
     *
     * The map has to exist first. This also runs as the component starts up, and the
     * invalidateSize behind it landing while Leaflet was still building itself left the map
     * unable to move at all.
     */
    $: if (map && $mapInUse !== mapWasInUse) {
        mapWasInUse = $mapInUse;
        mapLayoutChanged();
    }

    /**
     * Tells Leaflet that the map container has changed size because the sidebar came or
     * went. Nothing resizes the window, so the resize handler never hears about it, and a
     * map left thinking it is the old size draws empty tiles across the part it gained and
     * puts clicks in the wrong place.
     *
     * The wait is for the shell to lay itself out, since the class that moves the sidebar
     * comes off the same change as this. Leaflet keeps the centre of the map where it is,
     * so a dialog stays with the marker it belongs to as the width changes.
     */
    async function mapLayoutChanged() {
        await tick();
        resizeMap();

        // A dialog works out its size and its place when it opens, and on a narrow screen
        // that happens while the map is still put away behind the inbox and has no size to
        // speak of. Leaflet pans a dialog into view as it opens, which against a map of
        // nothing leaves it hanging off the side once the map is up. Measuring and placing
        // it again now that the map is there puts it back on screen.
        if (currentPopup) {
            currentPopup.update();
        }
    }

    /**
     * Puts back the focus ring Leaflet takes off the element a key event was aimed at.
     *
     * Leaflet hides the outline of that element on every key event that reaches the map
     * container, so that working the map with the keyboard doesn't leave a ring behind, and
     * restores it on the next keydown. Keypress and keyup get no such counterpart, so a text
     * field inside a popup ends up with its ring suppressed between one keydown and the next,
     * and it blinks with every character typed. Restoring once the event has passed the map
     * leaves nothing suppressed, and the keydown Leaflet handles itself is already back by
     * the time this runs.
     */
    function restoreFocusRing() {
        L.DomUtil.restoreOutline();
    }

    /**
     * Ends the map multi select mode, no questions asked.
     */
    function endMultiSelectMode() {
        $mapMultiSelectMode = false;
    }

    /**
     * Attempt to merge the selected playgrounds, provided two or more are selected and the user confirms.
     */
    const mergeSelectedPlaygrounds = runSelectionAction(async () => {
        if (selected.length < 2) {
            return;
        }

        if (!confirm('Slå ihop de markerade lekplatserna?')) {
            return;
        }

        let response = await ApiClient.mergePlaygrounds(selected);

        // Merge conflict
        if (response.status == 409) {
            let intro = 'En konflikt uppstod vid sammanslagningen';
            let message = PlaygroundHelper.presentMergeConflict(await response.json());

            alert(message ? `${intro}:\n\n${message}` : intro);
        } else if (!response.ok) {
            // Keep the selection, so that the merge can be retried
            throw new Error(`Merge failed: ${response.status} ${response.statusText}`);
        }

        endMultiSelectMode();
    });

    export function pressEnter() {
        if ($mapMultiSelectMode) {
            mergeSelectedPlaygrounds();
            return;
        }

        // Open dialog for new playground marker
        if (temporaryMarker) {
            temporaryMarker.fire('click', {
                originalEvent: {
                    isTrusted: true,
                },
            });
        }
    }

    export function pressEscape() {
        if ($mapMultiSelectMode) {
            endMultiSelectMode();
        }
    }
</script>

<!--
    The key handlers are not captured, so that they run after Leaflet has had the event and
    suppressed the focus ring of whatever it was aimed at.
-->
<svelte:window on:resize={resizeMap} on:keypress={restoreFocusRing} on:keyup={restoreFocusRing} />

<SearchBox
    hidden={$mapPlacingPlayground || $mapMultiSelectMode}
    dialogOpen={$mapPopupOpen}
    getCenter={searchCenter}
    selectPlace={goToPlace}
    selectPlayground={goToSearchedPlayground}
/>
<MapSelectionDialog bind:selected {endMultiSelectMode} {mergeSelectedPlaygrounds} busy={selectionBusy} />
<div class="map" use:mapAction />

<style>
    .map {
        height: 100%;
        width: 100%;
    }

    .map :global(.marker-text) {
        width: 100%;
        text-align: center;
        font-weight: 600;
        background-color: #444;
        color: #eee;
        border-radius: 0.5rem;
    }

    .map :global(.playground-marker) {
        width: 16px;
        height: 16px;
        background: orange;
        border: 1px solid black;
        top: -16px;
        left: -8px;
        transform: rotate(45deg);
        opacity: 0.75;
        border-radius: 25px 25px 0;
    }

    :global(.green) {
        background: greenyellow !important;
    }

    :global(.yellow) {
        background: yellow !important;
    }
</style>
